/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
        constructor() {
            super();
            this.section = this.closest('.js-product');
            this.productForm = this.section.querySelector('.js-product-form');
            this.optionSelectors = this.querySelectorAll('.option-selector');
            this.data = this.getProductData();

            this.updateAvailability();
            this.addEventListener('change', this.handleVariantChange.bind(this));

            this.scrollToSelectionModelOption();

        }

        /**
         * Updates the color option label text.
         * @param {object} evt - Event object
         */
        static updateLabelText(evt) {
            const selector = evt.target.closest('.option-selector');
            if (selector.dataset.selectorType === 'dropdown') return;

            const colorText = selector.querySelector('.js-color-text');
            if (!colorText) return;

            colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
        }

        /**
         * Handles 'change' events on the variant picker element.
         * @param {object} evt - Event object.
         */
        handleVariantChange(evt) {
            const productUrl = evt.target.dataset.productUrl;
            if (productUrl) {
                evt.target.checked = false;
                if (productUrl === window.location.href) {
                    location.reload();
                }
                window.location.href = productUrl;

                return;
            }
            const selectedOptions = this.getSelectedOptions();
            this.variant = null;

            // Get selected variant data (if variant exists).
            this.variant = this.data.product.variants.find((v) =>
                v.options.every((val, index) => val === selectedOptions[index])
            );

            if (this.variant) {
                const variantId = this.variant.id;
                const formattedVariantData = this.data.formatted[variantId];
                this.variant.inventory = formattedVariantData.inventory;
            }

            if (this.variant) {
                this.updateMedia();
                this.updateUrl(evt);
                this.updateVariantInput();
            }

            this.updateAddToCartButton();
            this.updateAvailability();
            this.updatePrice();
            this.updateBackorderText();
            this.updatePickupAvailability();
            this.updateSku();
            VariantPicker.updateLabelText(evt);

            this.dispatchEvent(new CustomEvent('on:variant:change', {
                bubbles: true,
                detail: {
                    form: this.productForm,
                    variant: this.variant,
                    product: this.data.product,
                    formatted: this.data.formatted[this.variant.id]
                }
            }));
        }

        /**
         * Updates the "Add to Cart" button label and disabled state.
         */
        updateAddToCartButton() {
            this.productForm = this.section.querySelector('.js-product-form');
            if (!this.productForm) return;

            this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
            const variantAvailable = this.variant && (this.variant.inventory > 0);
            const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

            this.addBtn.disabled = !variantAvailable;
            this.addBtn.textContent = variantAvailable
                ? this.addBtn.dataset.addToCartText
                : "Backorder";
        }

        /**
         * Updates the availability status in option selectors.
         */
        updateAvailability() {
            if (this.dataset.showAvailability === 'false') return;
            let currVariant = this.variant;

            if (!this.variant) {
                currVariant = {options: this.getSelectedOptions()};
            }

            const updateOptionAvailability = (optionEl, available, soldout) => {
                const el = optionEl;
                const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
                el.classList.toggle('is-unavailable', !available);

                if (optionEl.classList.contains('custom-select__option')) {
                    const em = el.querySelector('em');

                    if (em) {
                        em.hidden = available;
                    }

                    if (!available) {
                        if (em) {
                            em.textContent = text;
                        } else {
                            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
                        }
                    }
                } else if (!available) {
                    el.nextElementSibling.title = text;
                } else {
                    el.nextElementSibling.removeAttribute('title');
                }
            };

            // Flag all options as unavailable
            this.querySelectorAll('.js-option').forEach((optionEl) => {
                !optionEl.dataset.productUrl && updateOptionAvailability(optionEl, false, false);
            });

            // Flag selector options as available or sold out, depending on the variant availability
            this.optionSelectors.forEach((selector, selectorIndex) => {
                this.data.product.variants.forEach((variant) => {
                    let matchCount = 0;

                    variant.options.forEach((option, optionIndex) => {
                        if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                            matchCount += 1;
                        }
                    });

                    if (matchCount === currVariant.options.length - 1) {
                        const options = selector.querySelectorAll('.js-option');
                        const optionEl = Array.from(options).find((opt) => {
                            if (selector.dataset.selectorType === 'dropdown') {
                                return opt.firstElementChild.textContent === variant.options[selectorIndex];
                            }
                            let leftSide = opt.nextElementSibling.querySelector('.js-value').textContent;
                            let rightSide = variant.options[selectorIndex];
                            return (
                                leftSide ===
                                rightSide
                            );
                        });

                        if (optionEl) {
                            updateOptionAvailability(optionEl, variant.available, !variant.available);
                        }
                    }
                });
            });
        }

        /**
         * Updates the backorder text and visibility.
         */
        updateBackorderText() {
            this.backorder = this.backorder || this.section.querySelector('.backorder');
            if (!this.backorder) return;

            let hideBackorder = true;

            if (this.variant && this.variant.available) {
                const {inventory} = this.data.formatted[this.variant.id];

                if (this.variant.inventory_management && inventory === 'none') {
                    const backorderProdEl = this.backorder.querySelector('.backorder__product');
                    const prodTitleEl = this.section.querySelector('.product-title');
                    const variantTitle = this.variant.title.includes('Default')
                        ? ''
                        : ` - ${this.variant.title}`;

                    backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
                    hideBackorder = false;
                }
            }

            this.backorder.hidden = hideBackorder;
        }

        /**
         * Updates the product media.
         */
        updateMedia() {
            if (!this.variant.featured_media) return;

            if (this.section.matches('quick-add-drawer')) {
                this.section.updateMedia(this.variant.featured_media.id);
            } else {
                this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
                if (!this.mediaGallery) return;

                const variantMedia = this.mediaGallery.querySelector(
                    `[data-media-id="${this.variant.featured_media.id}"]`
                );
                this.mediaGallery.setActiveMedia(variantMedia, true, true);
            }
        }

        /**
         * Updates the pick up availability.
         */
        updatePickupAvailability() {
            this.pickUpAvailability =
                this.pickUpAvailability || this.section.querySelector('pickup-availability');
            if (!this.pickUpAvailability) return;

            if (this.variant && this.variant.available) {
                this.pickUpAvailability.getAvailability(this.variant.id);
            } else {
                this.pickUpAvailability.removeAttribute('available');
                this.pickUpAvailability.innerHTML = '';
            }
        }

        /**
         * Updates the price.
         */
        updatePrice() {
            this.price = this.price || this.section.querySelector('.product-info__price > .price');
            if (!this.price) return;

            if (this.variant) {
                const priceCurrentEl = this.price.querySelector('.price__current');
                const priceWasEl = this.price.querySelector('.price__was');
                const unitPriceEl = this.price.querySelector('.unit-price');

                // Update current price and original price if on sale.
                priceCurrentEl.textContent = this.data.formatted[this.variant.id].price;
                if (priceWasEl)
                    priceWasEl.textContent = this.data.formatted[this.variant.id].compareAtPrice || '';

                // Update unit price, if specified.
                if (this.variant.unit_price_measurement) {
                    const valueEl = this.price.querySelector('.unit-price__price');
                    const unitEl = this.price.querySelector('.unit-price__unit');
                    const value = this.variant.unit_price_measurement.reference_value;
                    const unit = this.variant.unit_price_measurement.reference_unit;

                    valueEl.textContent = this.data.formatted[this.variant.id].unitPrice;
                    unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
                }

                unitPriceEl.hidden = !this.variant.unit_price_measurement;
                this.price.classList.toggle(
                    'price--on-sale',
                    this.variant.compare_at_price > this.variant.price
                );
                this.price.classList.toggle('price--sold-out', !this.variant.available);
            }

            this.price.querySelector('.price__default').hidden = !this.variant;
            this.price.querySelector('.price__no-variant').hidden = this.variant;
        }

        /**
         * Updates the SKU.
         */
        updateSku() {
            this.sku = this.sku || this.section.querySelector('.product-sku__value') || this.section.querySelector("#sku-block");
            if (!this.sku) return;

            const skuAvailable = this.variant && this.variant.sku;
            this.sku.textContent = skuAvailable ? this.variant.sku : '';
            this.sku.parentNode.hidden = !skuAvailable;
        }

        /**
         * Updates the url with the selected variant id.
         * @param {object} evt - Event object.
         */
        updateUrl(evt) {
            if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
            window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
        }

        /**
         * Updates the value of the hidden [name="id"] form inputs.
         */
        updateVariantInput() {
            this.forms =
                this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

            this.forms.forEach((form) => {
                const input = form.querySelector('input[name="id"]');
                input.value = this.variant.id;
                input.dispatchEvent(new Event('change', {bubbles: true}));
            });
        }

        /**
         * Gets the selected option element from each selector.
         * @returns {Array}
         */
        getSelectedOptions() {
            const selectedOptions = [];

            this.optionSelectors.forEach((selector) => {
                if (selector.dataset.selectorType === 'dropdown') {
                    selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
                } else {
                    selectedOptions.push(selector.querySelector('input:checked')?.value);
                }
            });

            return selectedOptions;
        }

        /**
         * Gets the product data.
         * @returns {?object}
         */
        getProductData() {
            const dataEl = this.querySelector('[type="application/json"]');
            return JSON.parse(dataEl.textContent);
        }

        scrollToSelectionModelOption() {
            const selectedOptions = this.getSelectedOptions();
            console.log(selectedOptions);
            const selectedOption = selectedOptions.find(option => option !== 'Default Title');
            if (selectedOption) {
                const selectedOptionElement = this.querySelector(`label[data-option-value="${selectedOption}"]`);
                if (selectedOptionElement) {
                    selectedOptionElement.scrollIntoView({behavior: 'smooth', block: 'center'});
                }

            }
        }
    }


    customElements.define('variant-picker', VariantPicker);
}
